export const dateFormatter = new Intl.DateTimeFormat("en-US");

/**
 * Formats date using the specified tenant format
 *
 * @param date - Date to be formatted (string ISO or Date object)
 * @param dateFormat - Tenant date format with tokens:
 *   - YYYY/yyyy: 4-digit year (2025)
 *   - YY/yy: 2-digit year (25)
 *   - MM: 2-digit month (01-12)
 *   - DD/dd: 2-digit day (01-31)
 *   - HH: 2-digit hours 24h format (00-23)
 *   - hh: 2-digit hours 12h format (01-12) + AM/PM
 *   - mm: 2-digit minutes (00-59)
 *   - ss: 2-digit seconds (00-59)
 * @param includeTime - If true, includes time from format. If false, removes time tokens from format
 * @returns Formatted string or empty string if date is invalid
 *
 * @example
 * // Format with time, includeTime = true
 * formatDateWithTenantFormat("2025-10-30T14:30:45", "YYYY/MM/DD HH:mm:ss", true)
 * // Returns: "2025/10/30 14:30:45"
 *
 * @example
 * // Format with time, includeTime = false (removes time)
 * formatDateWithTenantFormat("2025-10-30T14:30:45", "YYYY/MM/DD HH:mm:ss", false)
 * // Returns: "2025/10/30"
 *
 * @example
 * // Format without time
 * formatDateWithTenantFormat("2025-10-30T14:30:45", "DD/MM/YYYY")
 * // Returns: "30/10/2025"
 */
export function formatDateWithTenantFormat(
  date: string | Date | null | undefined,
  dateFormat?: string,
  includeTime = false,
): string {
  if (!date) return "";

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) return "";

    let format = dateFormat || "MM/dd/yyyy";

    if (!includeTime) {
      format = format.replace(/\s*(HH|hh):mm(:ss)?.*$/, "").trim();
    }

    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    const hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");

    const hours12 = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    const hours12Str = String(hours12).padStart(2, "0");
    const hours24Str = String(hours).padStart(2, "0");

    let formattedDate = format
      .replace(/YYYY/g, String(year))
      .replace(/yyyy/g, String(year))
      .replace(/YY/g, String(year).slice(-2))
      .replace(/yy/g, String(year).slice(-2))
      .replace(/MM/g, month)
      .replace(/DD/g, day)
      .replace(/dd/g, day)
      .replace(/HH/g, hours24Str)
      .replace(/hh/g, hours12Str)
      .replace(/mm/g, minutes)
      .replace(/ss/g, seconds);

    if (format.includes("hh") && !format.includes("HH")) {
      formattedDate += ` ${ampm}`;
    }

    return formattedDate;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
}

/**
 * Formats product prices (already comes with decimals from the backend)
 * @param value - Product value
 * @param currency - Currency code (ex: "USD", "CAD", "EUR")
 * @returns Formatted string with currency symbol
 */
export function formatProductPrice(
  value: number | string | null | undefined,
  currency?: string,
): string {
  if (value === null || value === undefined || value === "") return "";

  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numericValue)) return "";

  const currencyCode = currency?.toUpperCase() || "USD";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  } catch (error) {
    console.error(
      "Error formatting product price:",
      error,
      "Currency:",
      currencyCode,
      "- Using fallback format",
    );
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericValue);
    } catch {
      return `$ ${numericValue.toFixed(2)}`;
    }
  }
}

/**
 * Formats order prices (comes in integer format from the backend)
 * @param value - Integer order value (no decimal places)
 * @param currency - Currency code (ex: "USD", "CAD", "EUR")
 * @returns Formatted string with currency symbol
 */
export function formatOrderPrice(
  value: number | null | undefined,
  currency?: string,
): string {
  if (value === null || value === undefined) return "";

  const currencyCode = currency?.toUpperCase() || "USD";

  try {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    });

    const options = formatter.resolvedOptions();
    const decimalPlaces = options.minimumFractionDigits || 2;

    const divisor = Math.pow(10, decimalPlaces);
    const decimalValue = value / divisor;

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(decimalValue);
  } catch (error) {
    console.error(
      "Error formatting order price:",
      error,
      "Currency:",
      currencyCode,
      "- Using fallback format",
    );
    try {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });

      const options = formatter.resolvedOptions();
      const decimalPlaces = options.minimumFractionDigits || 2;

      const divisor = Math.pow(10, decimalPlaces);
      const decimalValue = value / divisor;

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(decimalValue);
    } catch {
      return `$ ${(value / 100).toFixed(2)}`;
    }
  }
}

/**
 * Formats percentage
 * @param value - Percentage value
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with percentage symbol
 */
export function formatPercentage(
  value: number | null | undefined,
  decimals = 2,
): string {
  if (value === null || value === undefined) return "";

  return `${value.toFixed(decimals)}%`;
}
